'use client';

import Image from '@/components/Image';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';

interface DiamondProduct {
  id: number;
  shop_id?: number;
  shop_sku: string;
  name: string;
  short_name: string;
  introduction?: string;
  product_image: string;
  main_img?: string;
  price_info: any;
  price_info_v1?: any;
  price_info_v2?: any;
  price_info_v3?: any;
  discount_percent_v1?: number;
  discount_percent_v2?: number;
  discount_percent_v3?: number;
}

interface DiamondData {
  title: string;
  list: DiamondProduct[];
}

interface DivaLikeDiamondProps {
  pageData: { diamond: DiamondData };
  deviceType: EPlatform;
}

export default function DivaLikeDiamond({ pageData, deviceType }: DivaLikeDiamondProps) {
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const { navigate } = useAppNavigation();

  const isWap = deviceType !== EPlatform.WEB;

  // 使用公共的价格处理方法 - 与Vue版本的priceInfo方法一致
  const getPriceInfo = (item: DiamondProduct) => {
    return priceInfo(userInfo, item, currency);
  };

  // 商品点击处理 - 使用navigate方法统一处理跳转
  const handleProductClick = (item: DiamondProduct) => {
    navigate(`/goods-detail/${item.shop_sku}`);
  };

  if (isWap) {
    // 移动端布局 - 基于WAP版本LikeDianond.vue
    return (
      <div className="diva-like-dianond bg-black">
        <h2
          className="title font-bb font-bold text-[28px] text-[#DCB883] leading-[30px] pt-[20px] text-center"
          dangerouslySetInnerHTML={{ __html: pageData.diamond.title }}
        />
        <ul className="w-[346px] mx-auto pt-[10px]">
          {pageData.diamond.list.map((item, index) => {
            const currentPriceInfo = getPriceInfo(item);
            return (
              <li key={item.shop_id || item.id || index}>
                <a
                  href={`/goods-detail/${item.shop_sku}`}
                  className="product-item w-[346px] mt-[14px] relative inline-block"
                  onClick={(e) => {
                    e.preventDefault();
                    handleProductClick(item);
                  }}
                >
                  <Image
                    className="product-img w-full"
                    src={item.product_image || item.main_img}
                    alt={item.name}
                    width={346}
                    height={400}
                  />
                  <div className="product-info absolute h-[76px] left-0 right-0 bottom-0 p-[16px] box-border">
                    <div className="flex justify-between items-end">
                      <div className="short-name font-bb text-[24px] leading-[29px] text-primary-black">
                        {item.short_name}
                      </div>
                      <div className="price font-bb text-[20px] leading-[24px] text-[#DA974B] notranslate">
                        {formatPriceNumberWithSymbol(currentPriceInfo.sell_price || '0', currency)}
                      </div>
                    </div>
                    <div className="flex justify-between items-start mt-[6px]">
                      <div className="introduction font-br text-[12px] text-secondary-black leading-[12px] max-w-[200px]">
                        {item.introduction}
                      </div>
                      {currentPriceInfo.sell_price !== currentPriceInfo.price &&
                        currentPriceInfo.price && (
                          <div className="old-price font-br font-medium text-[15px] text-secondary-text leading-[9px] line-through notranslate">
                            {formatPriceNumberWithSymbol(currentPriceInfo.price, currency)}
                          </div>
                        )}
                    </div>
                  </div>
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  // PC端布局 - 基于PC版本LikeDianond.vue
  return (
    <div className="like-dianond bg-black pt-[1.56vw]">
      <h2
        className="title font-bb text-[3.75vw] text-[#DCB883] leading-[0.52vw] text-center"
        dangerouslySetInnerHTML={{ __html: pageData.diamond.title }}
      />
      <div className="product-row w-full px-[6.25vw] pt-[2.29vw] box-border flex justify-between">
        {pageData.diamond.list.map((item, index) => {
          const currentPriceInfo = getPriceInfo(item);
          return (
            <a
              key={item.shop_id || item.id || index}
              href={`/goods-detail/${item.shop_sku}`}
              className={`product-item inline-block w-[28.28vw] relative ${index < 2 ? 'mr-[0.83vw]' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                handleProductClick(item);
              }}
            >
              <Image
                className="product-img w-[28.28vw]"
                src={item.product_image || item.main_img}
                alt={item.name}
                width={543}
                height={600}
              />
              <div className="bottom-box w-[28.28vw] h-[6.04vw] box-border absolute px-[1.15vw] py-[1.35vw] left-0 right-0 bottom-0">
                <div className="flex justify-between items-end">
                  <div className="short-name font-bb text-[2.08vw] leading-[2.48vw] text-primary-black">
                    {item.short_name}
                  </div>
                  <div className="price font-bb text-[1.61vw] leading-[1.93vw] text-[#DA974B] notranslate">
                    {formatPriceNumberWithSymbol(currentPriceInfo.sell_price || '0', currency)}
                  </div>
                </div>
                <div className="flex justify-between items-start">
                  <div className="introduction font-br text-[0.99vw] text-secondary-black max-w-[16.67vw] box-border">
                    {item.introduction}
                  </div>
                  {currentPriceInfo.sell_price !== currentPriceInfo.price &&
                    currentPriceInfo.price && (
                      <div className="old-price font-br text-[1.25vw] text-secondary-text line-through notranslate">
                        {formatPriceNumberWithSymbol(currentPriceInfo.price, currency)}
                      </div>
                    )}
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}
